import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { LanguageSelector } from '@/components/LanguageSelector';
import { useLanguage } from '@/i18n/LanguageContext';
import { lc } from '@/i18n/copy';
import { useAuth } from '@/hooks/useAuth';
import { useUserCredits } from '@/hooks/useUserCredits';
import { Settings, LogOut, LayoutDashboard, Gift, Plus, Users, Lightbulb, ShieldCheck, Heart } from 'lucide-react';
import { useFounderStatus } from '@/hooks/useFounderStatus';
import { BrandLogo } from '@/components/BrandLogo';
import { supabase } from '@/integrations/supabase/client';

const copy = {
  en: { addCredits: 'Add Credits', settings: 'Settings', tagline: "While They're Still Here", tellFriend: 'Tell a Friend', shareIdea: 'Share an Idea', saved: 'Saved Messages' },
  es: { addCredits: 'Agregar Créditos', settings: 'Configuración', tagline: 'Mientras Aún Están Aquí', tellFriend: 'Invita a un Amigo', shareIdea: 'Comparte una Idea', saved: 'Mensajes Guardados' },
  fr: { addCredits: 'Ajouter des Crédits', settings: 'Paramètres', tagline: "Tant Qu'ils Sont Là", tellFriend: 'Parlez-en à un Ami', shareIdea: 'Partager une Idée', saved: 'Messages Sauvegardés' },
  pt: { addCredits: 'Adicionar Créditos', settings: 'Configurações', tagline: 'Enquanto Ainda Estão Aqui', tellFriend: 'Indique um Amigo', shareIdea: 'Compartilhar uma Ideia', saved: 'Mensagens Salvas' },
  ar: { addCredits: 'إضافة أرصدة', settings: 'الإعدادات', tagline: 'بينما هم لا يزالون هنا', tellFriend: 'أخبر صديقًا', shareIdea: 'شارك فكرة', saved: 'الرسائل المحفوظة' },
  ja: { addCredits: 'クレジット追加', settings: '設定', tagline: 'まだそばにいるうちに', tellFriend: '友達に教える', shareIdea: 'アイデアを共有', saved: '保存したメッセージ' },
  ko: { addCredits: '크레딧 추가', settings: '설정', tagline: '아직 곁에 있을 때', tellFriend: '친구에게 알리기', shareIdea: '아이디어 공유', saved: '저장된 메시지' },
  id: { addCredits: 'Tambah Kredit', settings: 'Pengaturan', tagline: 'Selagi Mereka Masih Ada', tellFriend: 'Beritahu Teman', shareIdea: 'Bagikan Ide', saved: 'Pesan Tersimpan' },
  hi: { addCredits: 'क्रेडिट जोड़ें', settings: 'सेटिंग्स', tagline: "While They're Still Here", tellFriend: 'एक दोस्त को बताएं', shareIdea: 'एक विचार साझा करें', saved: 'सहेजे गए संदेश' },
};

export function AuthenticatedHeader() {
  const { t, language } = useLanguage();
  const c = lc(copy, language);
  const { user, signOut } = useAuth();
  const { credits } = useUserCredits();
  const navigate = useNavigate();
  const hasPurchasedBundle = credits?.has_purchased_bundle ?? false;
  const { isActiveFounder } = useFounderStatus();
  const [profileOpen, setProfileOpen] = useState(false);
  const [tourLocked, setTourLocked] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [profileAvatarUrl, setProfileAvatarUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    supabase.rpc('has_role', { _user_id: user.id, _role: 'admin' }).then(({ data }) => {
      if (data) setIsAdmin(true);
    });
    // Fetch profile avatar
    supabase.from('profiles').select('avatar_url').eq('user_id', user.id).single().then(({ data }) => {
      if (data?.avatar_url) setProfileAvatarUrl(data.avatar_url);
    });
  }, [user]);

  // Listen for custom events from the guided tour to open/close the dropdown
  useEffect(() => {
    const handleTourOpen = () => {
      setProfileOpen(true);
      setTourLocked(true);
    };
    const handleTourClose = () => {
      setProfileOpen(false);
      setTourLocked(false);
    };

    window.addEventListener('tour:open-profile', handleTourOpen);
    window.addEventListener('tour:close-profile', handleTourClose);
    return () => {
      window.removeEventListener('tour:open-profile', handleTourOpen);
      window.removeEventListener('tour:close-profile', handleTourClose);
    };
  }, []);

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const initials = user?.user_metadata?.full_name
    ?.split(' ')
    .map((n: string) => n[0])
    .join('')
    .toUpperCase() || user?.email?.[0]?.toUpperCase() || 'U';

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border/20 glass">
      <div className="container flex h-20 items-center justify-between">
        <BrandLogo size="md" linkTo="/dashboard" />

        <nav className="hidden items-center gap-6 md:flex">
          <Link to="/dashboard" className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground">
            {t.nav.dashboard}
          </Link>
          {hasPurchasedBundle && (
            <Link to="/gift-store" className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground flex items-center gap-1.5">
              <Gift className="h-4 w-4" />
              {t.nav.giftStore}
            </Link>
          )}
          <Link to="/refer" className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground flex items-center gap-1.5">
            <Users className="h-4 w-4" />
            {c.tellFriend}
          </Link>
          {isActiveFounder && (
            <Link to="/founder-suggestions" className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground flex items-center gap-1.5">
              <Lightbulb className="h-4 w-4" />
              {c.shareIdea}
            </Link>
          )}
          {isAdmin && (
            <Link to="/admin" className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground flex items-center gap-1.5">
              <ShieldCheck className="h-4 w-4" />
              Admin
            </Link>
          )}
        </nav>

        <div className="flex items-center gap-3">
          <LanguageSelector />
          
          <DropdownMenu
            open={profileOpen}
            onOpenChange={(open) => {
              // Don't allow closing when the tour has locked it open
              if (tourLocked && !open) return;
              setProfileOpen(open);
            }}
            modal={false}
          >
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="relative h-10 w-10 rounded-full" data-tour="profile">
                <Avatar className="h-10 w-10">
                  <AvatarImage src={profileAvatarUrl || user?.user_metadata?.avatar_url} alt={user?.user_metadata?.full_name} />
                  <AvatarFallback className="bg-primary text-primary-foreground">{initials}</AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="w-56 bg-card border-border z-[9999]"
              align="end"
              forceMount
              data-tour="profile-menu"
              onInteractOutside={(e) => {
                if (tourLocked) e.preventDefault();
              }}
              onFocusOutside={(e) => {
                if (tourLocked) e.preventDefault();
              }}
            >
              <div className="flex items-center justify-start gap-2 p-2">
                <div className="flex flex-col space-y-1 leading-none">
                  {user?.user_metadata?.full_name && (
                    <p className="font-medium">{user.user_metadata.full_name}</p>
                  )}
                  <p className="text-xs text-muted-foreground">{user?.email}</p>
                </div>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to="/dashboard" className="cursor-pointer">
                  <LayoutDashboard className="mr-2 h-4 w-4" />
                  {t.nav.dashboard}
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/saved" className="cursor-pointer">
                  <Heart className="mr-2 h-4 w-4" />
                  {c.saved}
                </Link>
              </DropdownMenuItem>
              {hasPurchasedBundle && (
                <DropdownMenuItem asChild>
                  <Link to="/gift-store" className="cursor-pointer">
                    <Gift className="mr-2 h-4 w-4" />
                    {t.nav.giftStore}
                  </Link>
                </DropdownMenuItem>
              )}
              <DropdownMenuItem asChild>
                <Link to="/add-credits" className="cursor-pointer">
                  <Plus className="mr-2 h-4 w-4" />
                  {c.addCredits}
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/settings" className="cursor-pointer">
                  <Settings className="mr-2 h-4 w-4" />
                  {c.settings}
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleSignOut} className="cursor-pointer text-destructive">
                <LogOut className="mr-2 h-4 w-4" />
                {t.nav.logout}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
